// Type for API error objects (commonly from axios)
export interface ApiError {
  response?: {
    data?: {
      message?: string | Array<Record<string, string>>;
    };
    status?: number;
  };
  message?: string;
}

export interface FieldErrorMap {
  [key: string]: string;
}

/**
 * Extract field-level errors from API response
 * Supports:
 * 1) message: [{ field: "error message" }]
 * 2) message: "General error string"
 */
export const getFieldErrors = (error: ApiError): FieldErrorMap | null => {
  const message = error?.response?.data?.message;

  const fieldErrors: FieldErrorMap = {};

  // CASE 1: message = array of objects
  if (Array.isArray(message)) {
    message.forEach((errObj) => {
      if (typeof errObj === "object" && errObj !== null) {
        Object.entries(errObj).forEach(([field, msg]) => {
          if (typeof msg === "string") fieldErrors[field] = msg;
        });
      }
    });

    return Object.keys(fieldErrors).length ? fieldErrors : null;
  }

  // CASE 2: message = "Error Message"
  if (typeof message === "string") {
    return null; // no field errors, just general error
  }

  return null;
};

/**
 * Extract readable Api error message
 */
export const getApiErrorMessage = (error: ApiError): string => {
  if (!error?.response) {
    if (error?.message === "Network Error") {
      return "Network error — Please check your internet connection.";
    }

    if (error?.message?.includes("timeout")) {
      return "Request timed out — Please try again.";
    }

    if (error?.message?.includes("CORS")) {
      return "CORS error — server blocked the request.";
    }

    return error?.message || "Unable to reach the server.";
  }
  // ✔ Response exists → backend returned an error
  const status = error.response.status;
  const data = error.response.data;

  // Handler for string messages
  if (typeof data?.message === "string") return data.message;

  // Specific status codes
  if (status) {
    switch (status) {
      case 400:
        return "Bad Request — Please check your inputs.";
      case 401:
        return "Unauthorized — Please log in again.";
      case 403:
        return "You do not have permission to perform this action.";
      case 404:
        return "Resource not found.";
      case 408:
        return "Request timeout — Please try again.";
      case 413:
        return "File too large — upload a smaller file.";
      case 429:
        return "Too many requests — Please slow down.";
      default:
        if (status >= 500 && status <= 599) {
          return "Something went wrong - please try again after sometime.";
        }
        return "An error occurred while processing your request.";
    }
  }

  return "An error occurred while processing your request.";
};
